import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Shield, Calendar, Phone, Settings as SettingsIcon, Activity, ArrowLeft } from 'lucide-react';
import api, { getFileUrl } from '../utils/api';

const InfoItem = ({ icon, label, value, isId }) => (
    <div className="flex items-start gap-4 p-3.5 bg-gray-50 dark:bg-[#001f40]/40 rounded-xl border border-gray-100/50 dark:border-[#1a3884]/15 hover:shadow-sm transition-all duration-300">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-[#1a3884]/10 dark:from-blue-500/20 dark:to-[#1a3884]/20 flex items-center justify-center shrink-0 text-[#1a3884] dark:text-[#4f7dff]">
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
            <p className={`text-sm font-bold text-gray-800 dark:text-gray-200 truncate ${isId ? 'font-mono text-xs text-gray-500 dark:text-gray-400 font-normal' : ''}`}>{value}</p>
        </div>
    </div>
);

const Profile = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const [phoneValue, setPhoneValue] = useState('');

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const { data } = await api.get('/auth/me');
            if (data.success) {
                setProfileData(data.data);
            }
        } catch (error) {
            console.error('Failed to load profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const savePhoneNumber = async () => {
        const cleanPhone = phoneValue.replace(/\D/g, '').trim();
        if (cleanPhone.length !== 10) {
            alert('Phone number must contain exactly 10 digits.');
            return;
        }
        try {
            const { data } = await api.put('/auth/updatedetails', {
                mobile: cleanPhone
            });

            if (data.success) {
                setProfileData(data.data);
                setIsEditingPhone(false);
            }
        } catch (error) {
            console.error('Failed to update phone:', error);
            alert(error.response?.data?.message || 'Failed to update phone number');
        }
    };

    const uploadAdminImage = async (file) => {
        if (!file) return;

        const formData = new FormData();
        formData.append('profileImage', file);

        try {
            const { data } = await api.put('/auth/updatedetails', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (data.success) {
                setProfileData(data.data);
            }
        } catch (error) {
            console.error('Failed to upload image:', error);
            alert('Failed to upload image');
        }
    };

    const displayUser = profileData || user;

    if (!displayUser) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-[#1a3884]/20 border-t-[#1a3884] rounded-full animate-spin"></div>
                    <p className="text-gray-500 font-medium animate-pulse">Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            {/* Header Section matching Settings/Dashboard */}
            <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-xl px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147] animate-fade-in">
                <div className="flex items-center gap-4 text-left">
                    <button 
                        onClick={() => navigate(-1)} 
                        className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors"
                        title="Back"
                        aria-label="Go back"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-[#1a3884] dark:text-white mb-1 flex items-center gap-2">
                            <User className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                            Profile Overview
                        </h1>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium">
                                Manage your personal information and preferences
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Info Header Card */}
            <div className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-6 transition-all duration-300">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
                        {/* Profile Image / Logo */}
                        <div className="relative group shrink-0">
                            <div className="w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[#001f40] dark:to-[#002147] rounded-xl p-1 shadow-md border border-gray-100 dark:border-[#1a3884]/20 relative overflow-hidden flex items-center justify-center">
                                {displayUser?.role === 'admin' && displayUser?.profileImage ? (
                                    <img
                                        src={getFileUrl(displayUser.profileImage)}
                                        alt="Admin Profile"
                                        className="w-full h-full object-cover rounded-lg"
                                    />
                                ) : displayUser?.college?.logo ? (
                                    <img
                                        src={getFileUrl(displayUser.college.logo)}
                                        alt="College Logo"
                                        className="w-full h-full object-cover rounded-lg"
                                    />
                                ) : (
                                    <span className="bg-gradient-to-br from-[#1a3884] to-[#284BB3] text-transparent bg-clip-text text-4xl font-black">
                                        {(displayUser.fullName || displayUser.name || 'U').charAt(0)}
                                    </span>
                                )}

                                {displayUser?.role === 'admin' && (
                                    <label className="absolute bottom-1 right-1 bg-[#1a3884] text-white text-[10px] px-2 py-0.5 rounded cursor-pointer hover:bg-[#284BB3] transition-colors shadow-sm">
                                        Edit
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => uploadAdminImage(e.target.files[0])}
                                        />
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* User Details */}
                        <div className="flex-1 min-w-0">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                                {displayUser.fullName || displayUser.name}
                            </h2>
                            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mt-1">
                                {displayUser?.role === 'admin'
                                    ? 'SMAART Healthcare'
                                    : displayUser?.college?.name || ''}
                            </p>
                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2.5">
                                <span className="px-3 py-1 bg-blue-50 dark:bg-blue-950/40 text-[#1a3884] dark:text-[#4f7dff] text-xs font-bold rounded-full border border-blue-100/50 dark:border-blue-900/30 flex items-center gap-1">
                                    <Activity size={12} className="text-[#1a3884] dark:text-[#4f7dff]" />
                                    <span className="capitalize">{displayUser.role === 'admin' ? 'SMAART Admin' : displayUser.role}</span>
                                </span>
                                <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 text-xs font-bold rounded-full border border-emerald-100/50 dark:border-emerald-900/30 flex items-center gap-1 shadow-sm">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                    Active
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Edit Profile Button */}
                    <div className="shrink-0 flex items-center justify-center sm:justify-end">
                        <button
                            onClick={() => {
                                if (isEditingPhone) {
                                    savePhoneNumber();
                                } else {
                                    setPhoneValue(displayUser.mobile || displayUser.phone || '');
                                    setIsEditingPhone(true);
                                }
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-[#1a3884] to-[#284BB3] text-white hover:opacity-95 font-bold text-sm rounded-lg transition-all active:scale-95 flex items-center gap-2 shadow-sm border-0 cursor-pointer"
                        >
                            <SettingsIcon size={14} />
                            {isEditingPhone ? 'Save Profile' : 'Edit Profile'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Information Grid Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                {/* Personal Information */}
                <div className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-6 flex flex-col justify-between transition-all duration-300">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md">
                                <User className="text-white" size={18} />
                            </div>
                            <h3 className="text-lg font-bold text-[#1a3884] dark:text-white">Personal Information</h3>
                        </div>

                        <div className="space-y-4">
                            {/* Email Address */}
                            <InfoItem icon={<Mail size={18} />} label="Email Address" value={displayUser.email} />

                            {/* Phone Number */}
                            {isEditingPhone ? (
                                <div className="flex items-start gap-4 p-3.5 bg-gray-50 dark:bg-[#001f40]/40 rounded-xl border border-gray-100/50 dark:border-[#1a3884]/15 shadow-sm transition-all duration-300">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-[#1a3884]/10 dark:from-blue-500/20 dark:to-[#1a3884]/20 flex items-center justify-center shrink-0 text-[#1a3884] dark:text-[#4f7dff]">
                                        <Phone size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0 flex flex-col gap-1">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Phone Number</p>
                                        <div className="flex gap-2">
                                            <input
                                                value={phoneValue}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    setPhoneValue(val.slice(0, 10));
                                                }}
                                                maxLength={10}
                                                className="border dark:bg-[#00152e] dark:border-[#1a3884]/45 dark:text-white rounded-lg px-3 py-1.5 text-xs w-full outline-none focus:border-[#1a3884] dark:focus:border-[#4f7dff] transition-colors"
                                            />
                                            <button
                                                onClick={savePhoneNumber}
                                                disabled={phoneValue.replace(/\D/g, '').trim().length !== 10}
                                                className={`px-3 py-1.5 rounded-lg text-xs text-white font-bold transition-colors ${
                                                    phoneValue.replace(/\D/g, '').trim().length === 10
                                                        ? 'bg-[#1a3884] hover:bg-[#284BB3]'
                                                        : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed text-gray-500'
                                                }`}
                                            >
                                                Save
                                            </button>
                                        </div>
                                        {phoneValue.replace(/\D/g, '').trim().length !== 10 && (
                                            <p className="text-red-500 dark:text-red-400 text-[10px] font-semibold mt-1">
                                                Phone number must contain exactly 10 digits.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="group flex items-start gap-4 p-3.5 bg-gray-50 dark:bg-[#001f40]/40 rounded-xl border border-gray-100/50 dark:border-[#1a3884]/15 hover:shadow-sm transition-all duration-300">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-[#1a3884]/10 dark:from-blue-500/20 dark:to-[#1a3884]/20 flex items-center justify-center shrink-0 text-[#1a3884] dark:text-[#4f7dff]">
                                        <Phone size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0 flex items-center justify-between">
                                        <div>
                                            <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Phone Number</p>
                                            <p className="text-sm font-bold text-gray-800 dark:text-gray-200">
                                                {displayUser.mobile || displayUser.phone || 'Not provided'}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setPhoneValue(displayUser.mobile || displayUser.phone || '');
                                                setIsEditingPhone(true);
                                            }}
                                            className="text-[#1a3884] dark:text-[#4f7dff] font-bold text-xs hover:underline border-0 bg-transparent cursor-pointer"
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Member ID */}
                            <InfoItem icon={<User size={18} />} label="Member ID" value={displayUser.userId || displayUser._id} isId />
                        </div>
                    </div>
                </div>

                {/* Account Information */}
                <div className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/20 p-6 flex flex-col justify-between transition-all duration-300">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md">
                                <Shield className="text-white" size={18} />
                            </div>
                            <h3 className="text-lg font-bold text-[#1a3884] dark:text-white">Account Information</h3>
                        </div>

                        <div className="space-y-4">
                            {/* Joined Date */}
                            <InfoItem 
                                icon={<Calendar size={18} />} 
                                label="Joined Date" 
                                value={new Date(displayUser.createdAt || Date.now()).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })} 
                            />

                            {/* Access Role */}
                            <InfoItem 
                                icon={<Shield size={18} />} 
                                label="Access Role" 
                                value={
                                    displayUser?.role === 'admin'
                                        ? 'Smaart Admin'
                                        : displayUser?.role
                                        ? displayUser.role.charAt(0).toUpperCase() + displayUser.role.slice(1)
                                        : 'User'
                                } 
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
